import { AMERICAS, HIGHLIGHT } from "@/components/marketing/americasPaths";

// Real geography (Wikimedia low-res world map) cropped to Latin America; coordinates are in world.svg space.
const PINS = [
  { id: "mexico", x: 172, y: 252, label: "Mexico", lx: 12, ly: 4 },
  { id: "panama", x: 228, y: 312, label: "Panama", lx: 12, ly: 4 },
  { id: "peru", x: 238, y: 372, label: "Peru", lx: -40, ly: 4 },
  { id: "brazil", x: 302, y: 378, label: "Brazil", lx: 12, ly: 4 },
];
const ORIGIN = { x: 372, y: 228 };

export const LatamScene = ({ className = "" }) => (
  <svg viewBox="112 190 280 340" className={className} role="img" aria-label="Named patient supply from Dubai to Latin America" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="ls-blue" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#377DD3" />
        <stop offset="1" stopColor="#245FAF" />
      </linearGradient>
      <linearGradient id="ls-hl" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#4F93E0" />
        <stop offset="1" stopColor="#245FAF" />
      </linearGradient>
      <radialGradient id="ls-bg" cx="0.5" cy="0.45" r="0.6">
        <stop offset="0" stopColor="#EAF2FB" />
        <stop offset="1" stopColor="#EAF2FB" stopOpacity="0" />
      </radialGradient>
      <filter id="ls-shadow" x="-20%" y="-20%" width="140%" height="140%">
        <feDropShadow dx="0" dy="3" stdDeviation="3" floodColor="#245FAF" floodOpacity="0.35" />
      </filter>
    </defs>

    <circle cx="246" cy="350" r="190" fill="url(#ls-bg)" />
    <g stroke="#D7E3F2" strokeWidth="0.5" opacity="0.7">
      {[150, 200, 250, 300, 350].map((x) => <line key={x} x1={x} y1="190" x2={x} y2="530" />)}
      {[220, 270, 320, 370, 420, 470, 520].map((y) => <line key={y} x1="112" y1={y} x2="392" y2={y} />)}
    </g>

    {/* base land */}
    <g fill="#C9DBF0" stroke="#fff" strokeWidth="0.6" strokeLinejoin="round">
      {AMERICAS.map((d, i) => <path key={i} d={d} />)}
    </g>
    {/* highlighted destination countries */}
    <g fill="url(#ls-hl)" stroke="#fff" strokeWidth="0.8" strokeLinejoin="round" filter="url(#ls-shadow)">
      {Object.values(HIGHLIGHT).map((d, i) => <path key={i} d={d} />)}
    </g>

    {/* inbound lanes from Dubai (off-map, east) */}
    <g stroke="#F5C64D" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeDasharray="3 5">
      {PINS.map((p) => (
        <path key={p.id} d={`M${ORIGIN.x} ${ORIGIN.y} Q ${(ORIGIN.x + p.x) / 2 + 20} ${Math.min(ORIGIN.y, p.y) - 40} ${p.x} ${p.y}`} />
      ))}
    </g>
    <g transform={`translate(${ORIGIN.x} ${ORIGIN.y})`}>
      <circle r="9" fill="#F5C64D" opacity="0.3" />
      <circle r="5" fill="#F5C64D" />
      <circle r="2" fill="#0D1B2A" />
    </g>
    <g transform="translate(300 240)">
      <rect width="88" height="18" rx="9" fill="#0D1B2A" />
      <text x="44" y="12.2" textAnchor="middle" fontFamily="Inter, sans-serif" fontSize="8" fontWeight="700" fill="#fff">from Dubai DMCC</text>
    </g>

    {/* destination pins */}
    {PINS.map((p) => {
      const w = p.label.length * 5.6 + 14;
      const x = p.lx < 0 ? -w - 8 : 8;
      return (
        <g key={p.id} transform={`translate(${p.x} ${p.y})`}>
          <circle r="8" fill="#F5C64D" opacity="0.3" />
          <circle r="3.6" fill="#fff" stroke="#0D1B2A" strokeWidth="1.6" />
          <rect x={x} y="-8" width={w} height="16" rx="8" fill="#fff" stroke="#D7E3F2" strokeWidth="0.6" />
          <text x={x + w / 2} y="3" textAnchor="middle" fontFamily="Inter, sans-serif" fontSize="8" fontWeight="600" fill="#0D1B2A">{p.label}</text>
        </g>
      );
    })}

    {/* cold-chain delivery card */}
    <g transform="translate(236 436)">
      <rect width="152" height="88" rx="12" fill="#fff" stroke="#D7E3F2" strokeWidth="0.8" />
      <path d="M0 12a12 12 0 0 1 12-12h128a12 12 0 0 1 12 12v12H0Z" fill="url(#ls-blue)" />
      <text x="12" y="16" fontFamily="Inter, sans-serif" fontSize="8.5" fontWeight="700" fill="#fff">Named patient supply</text>
      <g transform="translate(12 34)">
        <rect width="20" height="20" rx="6" fill="#EAF2FB" />
        <path d="M10 5v10M5 10h10" stroke="#245FAF" strokeWidth="1.8" strokeLinecap="round" />
      </g>
      <text x="40" y="42" fontFamily="Inter, sans-serif" fontSize="7.5" fontWeight="700" fill="#0D1B2A">Originator product</text>
      <text x="40" y="52" fontFamily="Inter, sans-serif" fontSize="6.5" fill="#64748B">Shipped to final destination</text>
      <g transform="translate(12 64)">
        <rect width="128" height="15" rx="7.5" fill="#F8FAFC" stroke="#D7E3F2" strokeWidth="0.6" />
        <circle cx="9" cy="7.5" r="2.2" fill="#10B981" />
        <text x="16" y="10" fontFamily="Inter, sans-serif" fontSize="6.5" fontWeight="600" fill="#0D1B2A">Cold-chain · +2°C to +8°C</text>
      </g>
    </g>

    {/* plane on the inbound lane */}
    <g transform="translate(318 206) rotate(165) scale(1.5)">
      <path transform="translate(-12 -12)" d="M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z" fill="url(#ls-blue)" stroke="#fff" strokeWidth="0.8" strokeLinejoin="round" />
    </g>
  </svg>
);
